/* Marquee section behaviour. The scrolling itself is a CSS animation (section-marquee.css); this element
     - sets the animation duration from the width of one group and the speed (px per second) so every width scrolls at the
       same speed,
     - pauses the motion while the pointer is over the band or focus is inside it,
     - runs the pause / play button (WCAG 2.2.2) and keeps its label in step.
   Without JavaScript the band still scrolls (40s fallback) and has no working button. */
class MarqueeScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.marquee__track');
    this.group = this.querySelector('.marquee__group');
    this.toggle = this.querySelector('.marquee__toggle');
    if (!this.track || !this.group) return;

    this.userPaused = false;
    this.hovering = false;
    this.focused = false;

    this.setDuration();
    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(() => this.setDuration());
      this.observer.observe(this.group);
    }

    this.onEnter = () => {
      this.hovering = true;
      this.render();
    };
    this.onLeave = () => {
      this.hovering = false;
      this.render();
    };
    this.onFocusIn = (event) => {
      this.focused = true;
      // Only keyboard focus returns the track to its start. A mouse click also focuses a link (on mousedown), and moving the
      // track then would pull the link from under the pointer before mouseup, so the browser would drop the click.
      const link = event.target.closest && event.target.closest('.marquee__link');
      let keyboard = false;
      try {
        keyboard = Boolean(link && link.matches(':focus-visible'));
      } catch (error) {
        keyboard = false;
      }
      this.focusedLink = keyboard;
      this.render();
    };
    this.onFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.focused = false;
        this.focusedLink = false;
        this.render();
      }
    };
    this.addEventListener('mouseenter', this.onEnter);
    this.addEventListener('mouseleave', this.onLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);

    if (this.toggle) {
      this.onToggle = () => {
        this.userPaused = !this.userPaused;
        this.render();
      };
      this.toggle.addEventListener('click', this.onToggle);
    }
    this.render();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    this.removeEventListener('mouseenter', this.onEnter);
    this.removeEventListener('mouseleave', this.onLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    if (this.toggle && this.onToggle) this.toggle.removeEventListener('click', this.onToggle);
  }

  setDuration() {
    const width = this.group.getBoundingClientRect().width;
    const speed = Number(this.dataset.speed) || 50;
    if (!width) return;
    this.track.style.setProperty('--marquee-duration', `${Math.max(width / speed, 5)}s`);
  }

  render() {
    const paused = this.userPaused || this.hovering || this.focused;
    this.dataset.paused = String(paused);
    // the track returns to its start only for a focused link (the pause button does not need it)
    this.dataset.focused = String(Boolean(this.focused && this.focusedLink));
    this.dataset.userPaused = String(this.userPaused);
    if (this.toggle) {
      this.toggle.setAttribute('aria-label', this.userPaused ? this.dataset.playLabel : this.dataset.pauseLabel);
    }
  }
}

if (!customElements.get('marquee-scroller')) customElements.define('marquee-scroller', MarqueeScroller);
